import type { BadgeValue } from "@/types/dynamic-detail";
import { VersionStatus } from "./graph-view";

export type ScenarioSummary = {
    total: number;
    passed: number;
    failed: number;
    stale: boolean;
    passed_at?: string | null;
};

export type DeployedIn = {
    campaign_code: string;
    campaign_name: string;
    slot: string;
};

/** A campaign slot running one identified version of a workflow. */
export type WorkflowDeployment = DeployedIn & {
    version: number;
    version_id: number;
};

/** `GET /dialer/workflows/` row, and the `workflow` header of the lineage. */
export type WorkflowListItem = {
    id: number;
    code: string;
    name: string;
    description?: string | null;
    is_archived: boolean;
    forked_from?: number | null;
    forked_from_label?: string | null;
    version_count: number;
    latest_version?: { id: number; version: number; status: VersionStatus; status_badge?: BadgeValue } | null;
    deployed_in: WorkflowDeployment[];
    created_at?: string | null;
    updated_at?: string | null;
};

/** One entry of the lineage frieze. */
export type WorkflowVersionSummary = {
    id: number;
    workflow_id: number;
    workflow_code: string;
    workflow_name: string;
    version: number;
    status: VersionStatus;
    /** `{label, tone}` of `status`, served by the backend. */
    status_badge?: BadgeValue;
    notes?: string | null;
    parent_version?: number | null;
    parent_version_id?: number | null;
    created_by?: { id: number; name: string } | null;
    created_at: string;
    published_at?: string | null;
    scenarios: ScenarioSummary;
    deployed_in: DeployedIn[];
    cards: { active: number; total: number };
    cards_active: number;
    issue_counts: { error: number; warning: number };
    /** False when the version is published, deployed, or carries cards. */
    deletable: boolean;
};

/** `GET /dialer/workflows/:code/` */
/** A campaign slot offered as a deployment target, and what it runs today. */
export type DeployTargetOption = DeployedIn & {
    campaign_status?: string;
    current?: CampaignWorkflowSlot | null;
};

export type WorkflowLineage = {
    workflow: WorkflowListItem;
    versions: WorkflowVersionSummary[];
    /** Every non-archived campaign slot, free ones included. Served by the lineage. */
    deploy_targets?: DeployTargetOption[];
    permissions?: { edit: boolean; deploy: boolean; simulate: boolean };
};

/**
 * Every campaign slot a deployment of this workflow could target.
 *
 * The server sends the list, because only it can see the *free* slots: a workflow
 * somebody has just finished drawing runs nowhere, so a list derived from where this
 * workflow is already deployed is empty exactly when it matters most — and a brand new
 * branch got a disabled Deploy button with nothing explaining what to do about it.
 *
 * The derivation is kept as a fallback for a cached lineage read before the field
 * existed, and for a version screen that holds no lineage of its own.
 */
export function deployTargets(lineage: WorkflowLineage): DeployedIn[] {
    if (lineage.deploy_targets?.length) return lineage.deploy_targets;
    const map = new Map<string, DeployedIn>();
    (lineage.workflow?.deployed_in ?? []).forEach(target =>
        map.set(`${target.campaign_code}::${target.slot}`, { campaign_code: target.campaign_code, campaign_name: target.campaign_name, slot: target.slot })
    );
    lineage.versions.forEach(version => version.deployed_in.forEach(target => map.set(`${target.campaign_code}::${target.slot}`, target)));
    return [...map.values()];
}

export type DeployBlocker = {
    code: string;
    message: string;
    action?: { label: string; href: string } | null;
};

export type DeployWarning = {
    code: string;
    message: string;
};

export type InFlightPolicy = "pin" | "migrate";

export type VersionRef = {
    id: number;
    version: number;
    status: VersionStatus;
    /** `{label, tone}` of `status`, served by the backend. */
    status_badge?: BadgeValue;
    workflow_code?: string;
    workflow_name?: string;
};

/** The cards a deployment has to decide about, counted when the dialog asks. */
export type InFlightCounts = {
    active_cards: number;
    migratable: number;
    not_migratable: number;
    blocked_on_provider?: number;
    missing_node_ids: string[];
};

/** `GET /dialer/campaigns/:code/deploy-preview/` */
export type DeployPreview = {
    can_deploy: boolean;
    blockers: DeployBlocker[];
    warnings: DeployWarning[];
    scenarios: ScenarioSummary;
    slot: string;
    campaign_code: string;
    target_version: VersionRef;
    current_version?: VersionRef | null;
    in_flight: InFlightCounts;
    default_in_flight_policy: InFlightPolicy;
    /** True when this slot already ran this version: what makes a rollback a rollback. */
    was_deployed_before: boolean;
    change_summary?: string | null;
    permissions?: { edit: boolean; deploy: boolean; simulate: boolean };
};

/** One row of `GET /dialer/campaigns/:code/deployments/`. */
export type Deployment = {
    id: number;
    slot: string;
    from_version?: number | null;
    from_version_label?: string | null;
    to_version: number;
    to_version_label?: string | null;
    in_flight_policy: InFlightPolicy;
    is_rollback: boolean;
    reason?: string | null;
    in_flight_counts?: InFlightCounts | null;
    overridden_blockers?: DeployBlocker[] | null;
    created_by?: number | null;
    created_by_name?: string | null;
    created_at: string;
};

/**
 * `code vN` as `DeploymentSerializer` labels a version. The history rows carry the
 * version's primary key and this label, so the workflow code and the version number a
 * rollback needs are read back from it.
 */
export function parseVersionLabel(label: string | null | undefined): { code: string; version: number } | null {
    const match = (label ?? "").match(/^(.*) v(\d+)$/);
    if (!match) return null;
    return { code: match[1], version: Number(match[2]) };
}

/** `GET /dialer/campaigns/:code/deployments/` */
export type DeploymentHistory = {
    campaign_code: string;
    current: { slot: string; version?: VersionRef | null }[];
    deployments: Deployment[];
    permissions?: { edit: boolean; deploy: boolean; simulate: boolean };
};

/** A campaign slot, denormalised by `CampaignSerializer`. */
export type CampaignWorkflowSlot = {
    id: number;
    version: number;
    status: VersionStatus;
    workflow_code: string;
    workflow_name: string;
};

/** `GET /dialer/campaigns/` row and `GET /dialer/campaigns/:code/`. */
export type DialerCampaign = {
    id: number;
    code: string;
    name: string;
    description?: string | null;
    status: string;
    business_group?: string | null;
    main_workflow_version?: number | null;
    main_workflow?: CampaignWorkflowSlot | null;
    callback_workflow_version?: number | null;
    callback_workflow?: CampaignWorkflowSlot | null;
    in_flight_policy?: InFlightPolicy | null;
    default_calendar?: number | null;
    default_calendar_code?: string | null;
    max_concurrent_calls?: number | null;
    daily_call_cap?: number | null;
    expiration_policy?: string | null;
    expiration_delay?: string | null;
    active_cards: number;
    is_dispatchable: boolean;
    created_at?: string | null;
    updated_at?: string | null;
};

/** `GET /dialer/campaigns/:code/deployable-versions/` group. */
export type DeployableWorkflow = {
    workflow_code: string;
    workflow_name: string;
    versions: VersionRef[];
};

/** One reason a campaign cannot be activated, or one caveat about activating it. */
export type ReadinessIssue = {
    code: string;
    message: string;
    field?: string | null;
};

/** `GET /dialer/campaigns/:code/readiness/` */
export type CampaignReadiness = {
    campaign_code?: string;
    status?: string;
    is_dispatchable?: boolean;
    can_activate: boolean;
    blockers: ReadinessIssue[];
    warnings: ReadinessIssue[];
    permissions?: { deploy: boolean; edit?: boolean; simulate?: boolean };
};

/** `GET /dialer/workflow-versions/:id/scenarios/` row. */
export type Scenario = {
    id: number;
    name: string;
    status: "passed" | "failed" | "running" | "pending" | string;
    /** Last result as `{label, tone}` ("passed", "failed", "never run"), served by the backend. */
    result_badge?: BadgeValue;
    error?: string | null;
    expected_path: string[];
    asserts_delays: boolean;
    actual_path: string[];
    passed_at?: string | null;
    updated_at?: string | null;
    /** Edited since it last passed: its verdict no longer proves anything. */
    stale: boolean;
    /** Copied from the preceding immutable version; may be removed if no longer relevant. */
    is_carried: boolean;
};

/** `POST /dialer/workflow-versions/:id/run-scenarios/` */
/**
 * `POST /dialer/workflow-versions/:id/run-scenarios/` — the refreshed summary, which is
 * what tells the deployment dialog whether the gate just opened, plus one verdict per
 * replay.
 */
export type ScenarioRunReport = {
    version: VersionRef;
    summary: ScenarioSummary;
    passed_at?: string | null;
    results: {
        name: string;
        passed: boolean;
        summary?: string | null;
        path: string[];
        status: string;
        final_state?: string | null;
        failure?: string | null;
        mismatches: { code: string; message: string }[];
    }[];
};

/** The first step where a run left its expected path, derived for the report. */
export function firstDivergence(scenario: Scenario): { step: number; expected: string | null; got: string | null } | null {
    const expected = scenario.expected_path ?? [];
    const actual = scenario.actual_path ?? [];
    const length = Math.max(expected.length, actual.length);
    for (let step = 0; step < length; step += 1) {
        if (expected[step] !== actual[step]) return { step, expected: expected[step] ?? null, got: actual[step] ?? null };
    }
    return null;
}
